import React from 'react';
import {
  Box, Typography, Button, Paper, Menu, MenuItem, useMediaQuery, Pagination,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import Header from '../../components/common/Header';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PATHS } from '../../routes/paths';
import { getShopCategoriesApi, getShopListApi, getServerBaseUrl } from '../../api';

const PAGE_SIZE = 10;

const ShopList = () => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [activeCategory, setActiveCategory] = React.useState('latest');
  const [sort, setSort] = React.useState('latest');
  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);
  const [currentUser, setCurrentUser] = React.useState(null);
  const [shops, setShops] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const getPageFromSearch = React.useCallback(() => {
    const raw = searchParams.get('page');
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }, [searchParams]);
  const [page, setPage] = React.useState(getPageFromSearch);
  const [total, setTotal] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  
  const isMobile = useMediaQuery('(max-width:430px)');

  const updatePageParam = React.useCallback(
    (nextPage) => {
      const params = new URLSearchParams(searchParams);
      if (nextPage > 1) {
        params.set('page', String(nextPage));
      } else {
        params.delete('page');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        setCurrentUser(JSON.parse(raw));
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  const isAdmin = currentUser?.role === 'ADMIN';

  const serverUrl = React.useMemo(
    () => getServerBaseUrl(),
    [],
  );

  const getThumbnailUrl = (path) => {
    if (!path) return '/image/hotel1_image.png';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${serverUrl}${path}`;
  };

  const renderStars = (avgRating) => {
    const rating = Number(avgRating) || 0;

    if (rating <= 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
          {Array.from({ length: 5 }).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <StarBorderIcon key={idx} sx={{ fontSize: 14, color: '#d0d0d0' }} />
          ))}
        </Box>
      );
    }

    const full = Math.floor(rating);
    const hasHalf = rating - full >= 0.5;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
        {Array.from({ length: 5 }).map((_, index) => {
          const i = index + 1;
          if (i <= full) {
            return <StarIcon key={i} sx={{ fontSize: 14, color: '#ffb400' }} />;
          }
          if (hasHalf && i === full + 1) {
            return <StarHalfIcon key={i} sx={{ fontSize: 14, color: '#ffb400' }} />;
          }
          return <StarBorderIcon key={i} sx={{ fontSize: 14, color: '#d0d0d0' }} />;
        })}
      </Box>
    );
  };

  const loadCategories = React.useCallback(async () => {
    try {
      const res = await getShopCategoriesApi();
      const list = Array.isArray(res?.data) ? res.data : res;
      const normalized = (list || []).map((item) => ({
        id: item.categoryId ?? item.category_id ?? item.id,
        label: item.name,
        sortOrder: item.sortOrder ?? item.sort_order ?? 0,
      }));
      normalized.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCategories(normalized);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load shop categories:', err);
    }
  }, []);

  const fetchShops = React.useCallback(
    async (categoryKey, targetPage = 1) => {
      try {
        const categoryId = categoryKey === 'latest' ? undefined : categoryKey;
        const res = await getShopListApi({
          categoryId,
          page: targetPage,
          limit: PAGE_SIZE,
          sort,
        });
        const data = res?.data || res || {};
        setShops(data.items || []);
        setTotal(data.total ?? (data.items ? data.items.length : 0));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        if (err?.status === 401 || err?.suppressAlert) return;
      }
    },
    [sort],
  );

  React.useEffect(() => {
    loadCategories();
    fetchShops(activeCategory, page);
  }, [activeCategory, page, fetchShops, loadCategories]);

  React.useEffect(() => {
    if (activeCategory === 'latest') return;
    const exists = categories.some((item) => String(item.id) === String(activeCategory));
    if (!exists) {
      setActiveCategory('latest');
    }
  }, [activeCategory, categories]);

  React.useEffect(() => {
    const nextPage = getPageFromSearch();
    if (nextPage !== page) {
      setPage(nextPage);
    }
  }, [getPageFromSearch, page]);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleCategoryClick = (event, key) => {
    setActiveCategory(key);
    setPage(1);
    updatePageParam(1);
    if (key === 'latest') {
      setSortAnchorEl(event.currentTarget);
    } else {
      setSortAnchorEl(null);
    }
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (value) => {
    setSort(value);
    setSortAnchorEl(null);
    setPage(1);
    updatePageParam(1);
  };

  const handleClickShopCard = (shopId) => {
    if (!shopId) return;
    navigate(PATHS.shopDetail.replace(':id', String(shopId)), {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  const sortedShops = React.useMemo(() => {
    const list = [...shops];

    if (sort === 'latest') {
      list.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;

        if (aTime !== bTime) return bTime - aTime;

        const aId = Number(a.shopId) || 0;
        const bId = Number(b.shopId) || 0;
        return bId - aId;
      });
    } else if (sort === 'recommended') {
      list.sort((a, b) => {
        const aRating = Number(a.avgRating) || 0;
        const bRating = Number(b.avgRating) || 0;
        return bRating - aRating;
      });
    } else if (sort === 'reviews') {
      list.sort((a, b) => {
        const aCount = Number(a.reviewCount) || 0;
        const bCount = Number(b.reviewCount) || 0;
        return bCount - aCount;
      });
    }

    return list;
  }, [shops, sort]);

  const shopRows = React.useMemo(() => {
    const rows = [];
    for (let i = 0; i < sortedShops.length; i += 2) {
      rows.push(sortedShops.slice(i, i + 2));
    }
    return rows;
  }, [sortedShops]);

  const renderShopCard = (shop) => {
    const cardWidth = isMobile ? '100%' : '173px';
    const cardHeight = isMobile ? 'auto' : '172px';
    const imageHeight = isMobile ? '100px' : '111px';
    const textHeight = isMobile ? 'auto' : '61px';
    const fontSize = isMobile ? 12 : 13;
    const captionSize = isMobile ? 10 : 11;
    
    return (
      <Paper
        elevation={0}
        sx={{
          width: cardWidth,
          height: cardHeight,
          minWidth: isMobile ? 0 : '173px',
          maxWidth: isMobile ? 'none' : '173px',
          minHeight: isMobile ? 'auto' : '172px',
          maxHeight: isMobile ? 'none' : '172px',
          borderRadius: 2,
          border: '1px solid #e5e5e5',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          bgcolor: '#ffffff',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          boxSizing: 'border-box',
          m: 0,
          p: 0,
        }}
        onClick={() => handleClickShopCard(shop.shopId)}
      >
        <Box
          component="img"
          src={getThumbnailUrl(shop.thumbnailUrl)}
          alt={shop.name}
          sx={{
            width: '100%',
            height: imageHeight,
            minHeight: imageHeight,
            maxHeight: imageHeight,
            objectFit: 'cover',
            flexShrink: 0,
            display: 'block',
            m: 0,
            p: 0,
          }}
        />
        <Box sx={{ 
          height: textHeight,
          minHeight: textHeight,
          maxHeight: isMobile ? 'none' : textHeight,
          pt: isMobile ? 0.5 : 0.6, 
          pb: isMobile ? 1 : 1.2, 
          px: isMobile ? 1 : 1.2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
          overflow: 'hidden',
          m: 0,
        }}>
          <Typography
            variant="body2"
            sx={{ 
              fontWeight: 600, 
              fontSize: fontSize, 
              mb: 0.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
              m: 0,
              p: 0,
            }}
          >
            {shop.name}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.3,
            flexShrink: 0,
            m: 0,
            p: 0,
          }}>
            {renderStars(shop.avgRating)}
            <Typography
              variant="caption"
              sx={{ fontSize: captionSize, color: 'text.secondary', flexShrink: 0, m: 0, p: 0 }}
            >
              ({shop.reviewCount ?? 0})
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: isMobile ? 1.5 : 2,
          position: 'relative',
        }}
      >
        <MainMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onNavigate={handleNavigate}
        />

        <Header
          variant="full"
          onClickMenu={() => setMenuOpen(true)}
        />

        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, fontSize: 20 }}>
            유흥/골프/식당
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
            }}
          >
            {[{ key: 'latest', label: '최신순' }, ...categories.map((c) => ({ key: c.id, label: c.label }))].map((item) => (
              <Button
                key={item.key}
                variant={activeCategory === item.key ? 'contained' : 'outlined'}
                size="small"
                onClick={(e) => handleCategoryClick(e, item.key)}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.3,
                  fontSize: 12.5,
                  borderRadius: 3,
                  whiteSpace: 'nowrap',
                  boxShadow: activeCategory === item.key ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                  bgcolor: activeCategory === item.key ? '#111827' : '#ffffff',
                  color: activeCategory === item.key ? '#ffffff' : '#000000',
                  borderColor: '#e5e7eb',
                  '&:hover': {
                    bgcolor: activeCategory === item.key ? '#111827' : '#f9fafb',
                  },
                }}
              >
                {item.key === 'latest'
                  ? sort === 'latest'
                    ? '최신순'
                    : sort === 'recommended'
                    ? '별점순'
                    : sort === 'reviews'
                    ? '후기순'
                    : item.label
                  : item.label}
              </Button>
            ))}
          </Box>

          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={handleSortClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            disableScrollLock
            MenuListProps={{
              dense: true,
              sx: { p: 0 },
            }}
          >
            <MenuItem
              selected={sort === 'latest'}
              onClick={() => handleSortChange('latest')}
              sx={{ fontSize: 12, minHeight: 32 }}
            >
              최신순
            </MenuItem>
            <MenuItem
              selected={sort === 'recommended'}
              onClick={() => handleSortChange('recommended')}
              sx={{ fontSize: 12, minHeight: 32 }}
            >
              별점순
            </MenuItem>
            <MenuItem
              selected={sort === 'reviews'}
              onClick={() => handleSortChange('reviews')}
              sx={{ fontSize: 12, minHeight: 32 }}
            >
              후기순
            </MenuItem>
          </Menu>

          {shopRows.map((row, rowIndex) => {
            const gap = isMobile ? 1.2 : 2.5;
            const leftMargin = isMobile ? 0 : (row.length === 2 ? 2 : 2);
            const cardWidth = isMobile 
              ? `calc((100% - ${gap * 8}px) / 2)` 
              : '173px';
            
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={rowIndex}
                sx={{
                  display: 'flex',
                  gap: gap,
                  justifyContent: isMobile ? 'flex-start' : 'flex-start',
                  ml: leftMargin,
                  mb: rowIndex < shopRows.length - 1 ? (isMobile ? 1.5 : gap) : 0,
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                {row.map((shop) => (
                  <Box 
                    key={shop.shopId}
                    sx={{
                      width: cardWidth,
                      minWidth: isMobile ? 0 : '173px',
                      maxWidth: isMobile ? cardWidth : '173px',
                      height: isMobile ? 'auto' : '172px',
                      minHeight: isMobile ? 'auto' : '172px',
                      maxHeight: isMobile ? 'none' : '172px',
                      flexShrink: 0,
                      flexGrow: 0,
                      m: 0,
                      p: 0,
                    }}
                  >
                    {renderShopCard(shop)}
                  </Box>
                ))}
              </Box>
            );
          })}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_event, value) => {
                  setPage(value);
                  updatePageParam(value);
                }}
                size="small"
                siblingCount={0}
                boundaryCount={1}
              />
            </Box>
          )}
        </Box>
      </Box>

      {isAdmin && (
        <Box
          onClick={() => navigate(PATHS.adminShops)}
          sx={{
            position: 'fixed',
            bottom: 50,
            right: 'max(24px, calc((100vw - 430px) / 2 + 24px))',
            minWidth: 72,
            height: 40,
            px: 2.5,
            py: 1,
            borderRadius: 999,
            bgcolor: '#3679FF',
            color: '#ffffff',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 6px 12px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1200,
          }}
        >
          업체 관리
        </Box>
      )}
    </Box>
  );
};

export default ShopList;


